import { useEffect, useMemo, useState } from 'react';
import { addDays, addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isAfter, isBefore, isSameDay, isSameMonth, isToday, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CaretLeft, CaretRight, CalendarBlank } from 'phosphor-react';
import { Sheet } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';

interface Props {
  open: boolean;
  selectedFrom: string;
  selectedTo: string;
  onSelect: (from: string, to: string) => void;
  onClose: () => void;
  title?: string;
}

function toDate(value: string) {
  return parseISO(`${value}T12:00:00`);
}

const WEEK_START = startOfWeek(new Date(), { weekStartsOn: 1 });
const WEEK_DAYS = Array.from({ length: 7 }, (_, index) => format(addDays(WEEK_START, index), 'EEE', { locale: fr }));

export function EditorialDateRangePicker({
  open,
  selectedFrom,
  selectedTo,
  onSelect,
  onClose,
  title = 'Choisir une période',
}: Props) {
  const fromDate = useMemo(() => toDate(selectedFrom), [selectedFrom]);
  const toDateParsed = useMemo(() => toDate(selectedTo), [selectedTo]);
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(fromDate));
  const [pendingFrom, setPendingFrom] = useState<Date | null>(null);

  useEffect(() => {
    if (open) {
      setVisibleMonth(startOfMonth(fromDate));
      setPendingFrom(null);
    }
  }, [open, fromDate]);

  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(visibleMonth), { weekStartsOn: 1 }),
  }), [visibleMonth]);

  function handleDayClick(day: Date) {
    if (!pendingFrom) {
      setPendingFrom(day);
    } else {
      let from = pendingFrom;
      let to = day;
      if (isAfter(from, to)) {
        [from, to] = [to, from];
      }
      onSelect(format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd'));
      setPendingFrom(null);
      onClose();
    }
  }

  const rangeFrom = pendingFrom ?? fromDate;
  const rangeTo = pendingFrom ? null : toDateParsed;

  const fromLabel = format(fromDate, 'EEEE d MMMM yyyy', { locale: fr });

  return (
    <Sheet open={open} onClose={() => { setPendingFrom(null); onClose(); }} title={title}>
      <div className="space-y-5">
        {pendingFrom && (
          <p className="t-ui-sm text-ink-2 text-center">
            Sélectionnez la date de fin
          </p>
        )}

        <div className="rounded-xl bg-paper ring-1 ring-paper-edge p-4">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setVisibleMonth(current => addMonths(current, -1))}
              className="h-10 w-10 rounded-full grid place-items-center bg-paper-elev ring-1 ring-paper-edge"
            >
              <CaretLeft size={18} />
            </button>
            <div className="min-w-0 text-center">
              <div className="t-cap mb-1">Calendrier</div>
              <div className="t-ed-lg capitalize">{format(visibleMonth, 'LLLL yyyy', { locale: fr })}</div>
            </div>
            <button
              onClick={() => setVisibleMonth(current => addMonths(current, 1))}
              className="h-10 w-10 rounded-full grid place-items-center bg-paper-elev ring-1 ring-paper-edge"
            >
              <CaretRight size={18} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-7 gap-y-1 text-center">
            {WEEK_DAYS.map((day) => (
              <span key={day} className="t-cap text-ink-3">
                {day.slice(0, 3)}
              </span>
            ))}
            {days.map((day) => {
              const inMonth = isSameMonth(day, visibleMonth);
              const isFrom = isSameDay(day, rangeFrom);
              const isTo = rangeTo !== null && isSameDay(day, rangeTo);
              const inRange = rangeTo !== null && isAfter(day, rangeFrom) && isBefore(day, rangeTo);
              const today = isToday(day);
              const active = isFrom || isTo;
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => handleDayClick(day)}
                  className={[
                    'aspect-square flex items-center justify-center transition',
                    inRange ? 'bg-ink-1/10' : '',
                    active ? 'bg-ink-1 text-paper-elev shadow-sheet rounded-full' : 'text-ink-1',
                    !active && !inRange ? 'rounded-full' : '',
                    isFrom && !isSameDay(rangeFrom, rangeTo ?? rangeFrom) ? 'rounded-l-full rounded-r-none' : '',
                    isTo && !isSameDay(rangeFrom, rangeTo ?? rangeFrom) ? 'rounded-r-full rounded-l-none' : '',
                    !active && today ? 'ring-1 ring-champagne rounded-full' : '',
                    !inMonth ? 'text-ink-4' : '',
                    !active ? 'hover:bg-paper-rec' : '',
                  ].join(' ')}
                >
                  <span className={active ? 't-ui-lg' : 't-ui'}>{format(day, 'd')}</span>
                </button>
              );
            })}
          </div>
        </div>

        {!pendingFrom && (
          <div className="rounded-xl bg-paper-rec/55 ring-1 ring-paper-edge p-4 flex items-start gap-3">
            <div className="mt-0.5 h-10 w-10 rounded-full bg-paper-elev ring-1 ring-paper-edge grid place-items-center">
              <CalendarBlank size={18} />
            </div>
            <div className="min-w-0">
              <div className="t-cap mb-1">Période choisie</div>
              <div className="t-ed-md capitalize">
                {isSameDay(fromDate, toDateParsed)
                  ? fromLabel
                  : `${format(fromDate, 'EEEE d MMMM', { locale: fr })} → ${format(toDateParsed, 'EEEE d MMMM', { locale: fr })}`}
              </div>
            </div>
          </div>
        )}

        <Button variant="secondary" full onClick={() => {
          const today = format(new Date(), 'yyyy-MM-dd');
          onSelect(today, today);
          onClose();
        }}>
          Aujourd'hui
        </Button>
      </div>
    </Sheet>
  );
}
